import * as React from 'react';
import { useRouter, useSearch } from '@tanstack/react-router';

type SearchRecord = Record<string, unknown>;

/**
 * Route-agnostic view of `router.navigate`. TanStack Router types the search
 * reducer against the *registered* route tree (the root route's search is
 * `never`), which makes a generic, any-route filter write impossible to type
 * structurally. Filters is deliberately route-agnostic infrastructure, so we
 * narrow `navigate` to exactly the call we make. This is the single, contained
 * assertion that lets `shared/ui/filters` stay decoupled from route ids.
 */
type SearchNavigate = (opts: {
  search: (prev: SearchRecord) => SearchRecord;
  replace?: boolean;
}) => Promise<void>;

/**
 * Context for the `Filters` compound component. The URL search params are the
 * single source of truth (see CLAUDE.md state-boundary table); subcomponents
 * read/write them through this context.
 *
 * Filters is router-coupled by design: it calls `useNavigate`/`useSearch`
 * directly so a page gets URL-bound filters with zero wiring. `shared/ui`
 * depending on the router is fine — the router is ambient app infrastructure
 * (the same way `Link` is already used in widgets).
 */
export interface FiltersContextValue {
  /** Current value of a search param (stringified), or undefined if unset. */
  getParam: (key: string) => string | undefined;
  /** Current value of a list-typed search param. */
  getListParam: (key: string) => string[];
  /** Set or clear a param. Passing undefined/empty removes it from the URL. */
  setParam: (key: string, value: string | string[] | undefined) => void;
  /** Remove every managed param (and reset pagination). */
  clearAll: () => void;
  /** True if any of the given keys currently has a value. */
  hasAny: (keys: string[]) => boolean;
}

const FiltersContext = React.createContext<FiltersContextValue | null>(null);

export function useFiltersContext(): FiltersContextValue {
  const ctx = React.useContext(FiltersContext);
  if (!ctx) {
    throw new Error('Filters.* components must be used within <Filters.Root>.');
  }
  return ctx;
}

const LIST_SEPARATOR = ',';

interface FiltersProviderProps {
  /** Param keys this Filters group manages (used by Clear / hasAny). */
  managedKeys: string[];
  /** Param reset to 1 whenever a filter changes (table pagination). */
  pageKey?: string;
  children: React.ReactNode;
}

export function FiltersProvider({
  managedKeys,
  pageKey = 'page',
  children,
}: FiltersProviderProps) {
  // `strict: false` reads the search of whichever route is active, so Filters
  // works on any page without knowing the route id.
  const search: SearchRecord = useSearch({ strict: false });
  const router = useRouter();
  // See SearchNavigate above for why this single assertion is necessary.
  const navigate = router.navigate as SearchNavigate;

  const value = React.useMemo<FiltersContextValue>(() => {
    const getParam = (key: string): string | undefined => {
      const raw = search[key];
      // Search-param primitives only; ignore anything object-shaped.
      if (typeof raw === 'string') return raw;
      if (typeof raw === 'number' || typeof raw === 'boolean') return String(raw);
      return undefined;
    };

    const getListParam = (key: string): string[] => {
      const raw = search[key];
      if (Array.isArray(raw)) {
        return raw.filter((v): v is string => typeof v === 'string');
      }
      if (typeof raw === 'string' && raw.length > 0) return raw.split(LIST_SEPARATOR);
      return [];
    };

    const patch = (next: SearchRecord): void => {
      void navigate({
        // Reset pagination on any filter change — but only when the page param
        // is actually in use, so pages without pagination (e.g. Orders) don't
        // accrue a stray `page=1`. Spread the previous search first.
        search: (prev: SearchRecord): SearchRecord => ({
          ...prev,
          ...(prev[pageKey] !== undefined ? { [pageKey]: 1 } : {}),
          ...next,
        }),
        replace: true,
      });
    };

    const setParam = (key: string, value: string | string[] | undefined): void => {
      const normalized =
        value === undefined ||
        value === '' ||
        (Array.isArray(value) && value.length === 0)
          ? undefined
          : Array.isArray(value)
            ? value.join(LIST_SEPARATOR)
            : value;
      patch({ [key]: normalized });
    };

    const clearAll = (): void => {
      patch(Object.fromEntries(managedKeys.map((key) => [key, undefined])));
    };

    const hasAny = (keys: string[]): boolean =>
      keys.some((key) => {
        const raw = search[key];
        return raw !== undefined && raw !== null && raw !== '';
      });

    return { getParam, getListParam, setParam, clearAll, hasAny };
  }, [search, navigate, managedKeys, pageKey]);

  return <FiltersContext.Provider value={value}>{children}</FiltersContext.Provider>;
}
